import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { listProjects } from '@/services/projectService';
import {
  getScriptInstrumentation,
  type ScriptInstrumentationRow,
} from '@/services/adminService';
import type { ScriptProject } from '@/types/scriptProject';
import { echoScoreDisplay } from '@/config/indexThresholds';
import { formatMandateId, formatScriptId } from '@/utils/formatIndexId';

function formatCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return String(value);
}

function formatEngagement(score: number | null | undefined): string {
  if (score == null || Number.isNaN(score)) return '—';
  const display = echoScoreDisplay(score);
  return display != null ? display.toFixed(1) : '—';
}

export default function ScriptInstrumentationPanel() {
  const [projects, setProjects] = useState<ScriptProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [rows, setRows] = useState<ScriptInstrumentationRow[]>([]);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    setError(null);
    try {
      const list = await listProjects();
      setProjects(list);
      setSelectedProjectId((prev) => {
        if (prev && list.some((p) => p.id === prev)) return prev;
        return list[0]?.id ?? null;
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load mandates');
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  const loadInstrumentation = useCallback(async (projectId: string) => {
    setLoadingScripts(true);
    setError(null);
    try {
      const data = await getScriptInstrumentation(projectId);
      setRows(data.scripts);
      setProjectName(data.project_name ?? null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load script instrumentation');
      setRows([]);
    } finally {
      setLoadingScripts(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (!selectedProjectId) {
      setRows([]);
      return;
    }
    void loadInstrumentation(selectedProjectId);
  }, [selectedProjectId, loadInstrumentation]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );

  const completedCount = rows.filter((r) => r.status === 'complete').length;
  const rowsWithErrors = rows.filter((r) => (r.critical_errors?.length ?? 0) > 0);
  const analysedRows = rows.filter((r) => r.status === 'complete');
  const rowsWithEmissionIssues = analysedRows.filter(
    (r) => !r.emission || r.emission.has_issues === true,
  );
  const allEmissionKnown = analysedRows.length > 0 && analysedRows.every((r) => r.emission != null);

  const refresh = () => {
    void loadProjects();
    if (selectedProjectId) void loadInstrumentation(selectedProjectId);
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-mono text-white/45 max-w-2xl">
          Per-script pipeline counts, engagement score, emission health, and critical errors by
          development mandate.
        </p>
        <Button
          variant="indexOutline"
          size="sm"
          onClick={refresh}
          disabled={loadingProjects || loadingScripts}
        >
          Refresh
        </Button>
      </div>

      {loadingProjects && (
        <div className="ni-panel py-16 text-center text-white/45 font-sans">Loading mandates…</div>
      )}

      {error && (
        <div className="mb-6 ni-panel border-red-500/30 px-4 py-3 text-red-300 text-sm">{error}</div>
      )}

      {!loadingProjects && projects.length === 0 && (
        <div className="ni-panel py-16 text-center text-white/45 font-sans">
          No development mandates registered.
        </div>
      )}

      {!loadingProjects && projects.length > 0 && (
        <>
          <section className="mb-6">
            <p className="ni-label mb-3">Development mandate</p>
            <div className="ni-panel divide-y divide-[#1f1f1f]">
              {projects.map((project) => {
                const active = project.id === selectedProjectId;
                return (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => setSelectedProjectId(project.id)}
                    className={cn(
                      'w-full flex items-center gap-2 px-4 py-3 text-left border-l-2 border-transparent transition-colors',
                      active ? 'ni-mandate-row-active' : 'hover:bg-white/[0.02]',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono text-white/70">
                        <span className="text-index-accent">{formatMandateId(project.id)}</span>
                        {' · '}
                        <span className="text-white">{project.name}</span>
                      </p>
                      <p className="text-[10px] font-mono text-white/35 mt-1">
                        {project.script_count ?? 0} scripts
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {selectedProject && (
            <section className="ni-panel overflow-hidden">
              <div className="px-5 py-4 border-b border-[#2a2a2a] flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="ni-label">Scripts in mandate</p>
                  <p className="text-xs font-mono text-white/40 mt-1">
                    {projectName ?? selectedProject.name} · {completedCount} analysed
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-sans min-w-[960px]">
                  <thead>
                    <tr className="text-left text-white/45 border-b border-[#2a2a2a]">
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Script</th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider text-right">
                        Engagement
                      </th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider text-right">
                        Scenes
                      </th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider text-right">
                        Events
                      </th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider text-right">
                        Candidate beats
                      </th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider text-right">
                        Themes
                      </th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider text-right">
                        Qualified beats
                      </th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider text-right">
                        Confirmed beats
                      </th>
                      <th className="px-5 py-3 font-mono text-xs uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingScripts && (
                      <tr>
                        <td colSpan={9} className="px-5 py-12 text-center text-white/45">
                          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                          Loading script metrics…
                        </td>
                      </tr>
                    )}
                    {!loadingScripts && rows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-5 py-8 text-center text-white/45">
                          No scripts in this mandate yet.
                        </td>
                      </tr>
                    )}
                    {!loadingScripts &&
                      rows.map((row, index) => {
                        const id = row.analysis_session_id || row.session_id;
                        const instr = row.instrumentation;
                        return (
                          <tr
                            key={id ?? `row-${index}`}
                            className="border-b border-[#1f1f1f] text-white/80"
                          >
                            <td className="px-5 py-3">
                              <span className="block text-sm">{row.script_title || 'Untitled'}</span>
                              {id && (
                                <span className="block text-[10px] font-mono text-white/35 mt-0.5">
                                  {formatScriptId(id)}
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3 text-right font-mono tabular-nums text-index-cleared">
                              {formatEngagement(instr?.engagement_score)}
                            </td>
                            <td className="px-5 py-3 text-right font-mono tabular-nums">
                              {formatCount(instr?.total_scenes)}
                            </td>
                            <td className="px-5 py-3 text-right font-mono tabular-nums">
                              {formatCount(instr?.total_events)}
                            </td>
                            <td className="px-5 py-3 text-right font-mono tabular-nums">
                              {formatCount(instr?.total_candidate_beats)}
                            </td>
                            <td className="px-5 py-3 text-right font-mono tabular-nums">
                              {formatCount(instr?.total_themes)}
                            </td>
                            <td className="px-5 py-3 text-right font-mono tabular-nums">
                              {formatCount(instr?.qualified_beat_count)}
                            </td>
                            <td className="px-5 py-3 text-right font-mono tabular-nums">
                              {formatCount(instr?.confirmed_beat_count)}
                            </td>
                            <td className="px-5 py-3 capitalize text-xs text-white/50">
                              {row.status === 'complete' ? 'Analysed' : row.status.replace('_', ' ')}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {selectedProject && !loadingScripts && (
            <section className="ni-panel mt-6 overflow-hidden border-amber-500/30">
              <div className="px-5 py-4 border-b border-amber-500/20">
                <p className="ni-label text-amber-200">Score & payload emission</p>
                <p className="text-xs font-mono text-white/40 mt-1">
                  Flags analysed scripts where V7 sub-metric scores or scorecard payload fields did
                  not emit a value.
                </p>
              </div>
              <div className="px-5 py-4">
                {rowsWithEmissionIssues.length === 0 && allEmissionKnown ? (
                  <p className="text-sm font-mono text-white/40">
                    All analysed scripts in this mandate emitted scores and payload fields.
                  </p>
                ) : rowsWithEmissionIssues.length === 0 ? (
                  <p className="text-sm font-mono text-white/40">
                    Emission checks are unavailable for analysed scripts in this mandate (API module
                    missing or not yet redeployed).
                  </p>
                ) : (
                  <ul className="space-y-4">
                    {rowsWithEmissionIssues.map((row, index) => {
                      const id = row.analysis_session_id || row.session_id;
                      const emission = row.emission;
                      return (
                        <li
                          key={id ?? `emit-${index}`}
                          className="text-sm border-b border-[#1f1f1f] pb-4 last:border-0"
                        >
                          <div className="flex flex-wrap items-baseline gap-2 mb-2">
                            <span className="text-white/90 font-medium">
                              {row.script_title || 'Untitled'}
                            </span>
                            {id && (
                              <span className="text-[10px] font-mono text-white/35">
                                {formatScriptId(id)}
                              </span>
                            )}
                            <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300/90">
                              {emission ? 'Issue' : 'Unavailable'}
                            </span>
                          </div>
                          {!emission ? (
                            <p className="text-xs font-mono text-white/50">
                              Emission health could not be computed for this script. Redeploy the API
                              with scorecard emission checks enabled.
                            </p>
                          ) : null}
                          {emission?.missing_scores && emission.missing_scores.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] font-mono uppercase tracking-wider text-white/45 mb-1">
                                Missing scores ({emission.missing_scores.length})
                              </p>
                              <p className="text-xs font-mono text-amber-200/90">
                                {emission.missing_scores.join(' · ')}
                              </p>
                            </div>
                          )}
                          {emission?.missing_payload && emission.missing_payload.length > 0 && (
                            <div className="mb-2">
                              <p className="text-[10px] font-mono uppercase tracking-wider text-white/45 mb-1">
                                Missing payload ({emission.missing_payload.length})
                              </p>
                              <p className="text-xs font-mono text-amber-200/90">
                                {emission.missing_payload.join(' · ')}
                              </p>
                            </div>
                          )}
                          {emission?.missing_agent_fields &&
                            emission.missing_agent_fields.length > 0 && (
                              <div>
                                <p className="text-[10px] font-mono uppercase tracking-wider text-white/45 mb-1">
                                  Missing agent scorecard-json ({emission.missing_agent_fields.length})
                                </p>
                                <p className="text-xs font-mono text-amber-200/80">
                                  {emission.missing_agent_fields.slice(0, 6).join(' · ')}
                                  {emission.missing_agent_fields.length > 6
                                    ? ` · +${emission.missing_agent_fields.length - 6} more`
                                    : ''}
                                </p>
                              </div>
                            )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
          )}

          {selectedProject && !loadingScripts && (
            <section className="ni-panel mt-6 overflow-hidden border-red-500/30">
              <div className="px-5 py-4 border-b border-red-500/20">
                <p className="ni-label text-red-300">Critical errors</p>
                <p className="text-xs font-mono text-white/40 mt-1">
                  Scoring sections that failed after retries. The engagement score is still
                  published as a partial score.
                </p>
              </div>
              <div className="px-5 py-4">
                {rowsWithErrors.length === 0 ? (
                  <p className="text-sm font-mono text-white/40">
                    No critical errors for analysed scripts in this mandate.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {rowsWithErrors.map((row, index) => {
                      const id = row.analysis_session_id || row.session_id;
                      return (
                        <li key={id ?? `err-${index}`} className="text-sm">
                          <span className="text-white/80">{row.script_title || 'Untitled'}</span>
                          {id && (
                            <span className="ml-2 text-[10px] font-mono text-white/35">
                              {formatScriptId(id)}
                            </span>
                          )}
                          <ul className="mt-1 ml-4 list-disc space-y-0.5">
                            {(row.critical_errors ?? []).map((err, i) => (
                              <li key={i} className="text-xs font-mono text-red-300/90">
                                {err}
                              </li>
                            ))}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
